import { Box, Flex, Text } from '@chakra-ui/react';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Banner, Button, ButtonText, useToast } from '@morressier/alembic';
import {
  IOrganization,
  ISubmissionContextConfig,
  ISubmissionFile
} from '@publishing-engine/domain/models';
import {
  FormFieldFileUpload,
  FormFieldSubmittingAuthor
} from '@publishing-engine/domain/types/article';
import { GenericFormFields } from '@publishing-engine/submission-service/src/workflow-configuration/generic-form-fields/generic-form-fields';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSubmittingAuthorOnlySubmitCopyrightForm } from 'src/api/commands';
import { useKeycloak } from 'src/components/AuthHOC';
import { FormFieldSingleFileUpload } from 'src/components/FormField';
import ProtectedAction from 'src/components/ProtectedAction';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { downloadFile } from 'src/utils/file';

type Props = {
  organizationData: Pick<IOrganization, 'name'> | undefined;
  config: ISubmissionContextConfig;
  refetch: () => void;
};

type ISubmittingAuthorOnlyCopyrightForm = {
  authorCopyrightForm: {
    file: ISubmissionFile;
  };
};

const SubmittingAuthorOnlyCopyrightForm: React.FC<Props> = ({
  organizationData,
  config,
  refetch
}) => {
  const {
    submissionId,
    workflowResponse,
    submissionContextId,
    organizationId,
    fieldsResponse
  } = useSubmissionData();

  const { getFieldState } = useSubmissionFields();
  const { currentUser } = useKeycloak();
  const { articleWorkflowId } = fieldsResponse;
  const toast = useToast();
  const authorUploadCopyrightSubmissionForm =
    workflowResponse.forms.authorUploadCopyright;

  if (!authorUploadCopyrightSubmissionForm) {
    return;
  }

  const copyrightFormFields = new GenericFormFields(
    authorUploadCopyrightSubmissionForm
  );

  const submittingAuthorField =
    getFieldState<FormFieldSubmittingAuthor>('SubmittingAuthor')!;

  const configs = {
    AuthorCopyrightFormFile: copyrightFormFields.getFieldConfig(
      'AuthorCopyrightFormFile'
    ) as FormFieldFileUpload
  };

  const methods = useForm<ISubmittingAuthorOnlyCopyrightForm>();

  const { handleSubmit, getValues } = methods;

  const submittingAuthorOnlyCopyrightSubmissionMutation =
    useSubmittingAuthorOnlySubmitCopyrightForm({
      submissionId,
      organizationId,
      submissionContextId,
      articleWorkflowId
    });
  const { authorCopyrightForm } = getValues();
  const isDisable = !authorCopyrightForm?.file?.url;

  const submit = async () => {
    const { authorCopyrightForm } = getValues();

    const formPayload = {
      authorCopyrightForm: {
        ...authorCopyrightForm,
        email: submittingAuthorField.value,
        userId: currentUser.id,
        isSubmittingAuthor: true
      }
    };
    const response =
      await submittingAuthorOnlyCopyrightSubmissionMutation.mutateAsync(
        formPayload
      );
    if (response.isSuccess) {
      toast({
        variant: 'success',
        title: 'Copyright and Publication Agreement Concluded',
        description: (
          <span>
            You will receive a confirmation email with a copy of the agreement
            after submission.
          </span>
        ),
        isClosable: true
      });
      refetch();
    } else {
      toast({
        variant: 'error',
        title: 'Copyright and Publication Agreement Conclusion Failed',
        description: 'Please try again later.'
      });
    }
  };

  const [isFileDownloading, setIsFileDownloading] = useState(false);

  const handleDownload = async (fileUrl: string) => {
    try {
      setIsFileDownloading(true); // disable button
      await downloadFile(fileUrl);
    } finally {
      setIsFileDownloading(false); // re-enable no matter what
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submit)}>
        <Flex flexDirection="column" gap={8}>
          <Box>
            <Flex flexDirection="column" gap={8}>
              <Flex flexDirection="column" gap={8}>
                <Flex flexDirection="column" gap={2}>
                  <Text textStyle="headingSection">
                    Copyright and Publication Agreement
                  </Text>
                  <Text textStyle="textBody">
                    As the submitting author, please download and upload the
                    required agreement for this submission:
                  </Text>
                </Flex>

                <Banner
                  heading=""
                  content={
                    <Flex flexDirection="row">
                      <Text textStyle="headingItem">
                        Download The {organizationData?.name ?? ''} Agreement
                        First:{' '}
                      </Text>
                      <ButtonText
                        variant="primary"
                        onClick={() => {
                          if (config.authorCopyrightFormUrl) {
                            handleDownload(config.authorCopyrightFormUrl);
                          }
                        }}
                        isDisabled={isFileDownloading}
                      >
                        Download Agreement
                      </ButtonText>
                    </Flex>
                  }
                  customIcon={<FontAwesomeIcon icon={faTriangleExclamation} />}
                  variant="attention"
                />
              </Flex>

              {configs.AuthorCopyrightFormFile && (
                <>
                  <FormFieldSingleFileUpload
                    submissionContextId={submissionContextId}
                    methods={methods}
                    config={configs.AuthorCopyrightFormFile}
                  />
                </>
              )}

              <Flex width="100%" justifyContent="flex-start" gap={4}>
                <ProtectedAction
                  relation="author"
                  entityType="submission"
                  entityId={submissionId}
                >
                  <Button
                    variant="secondary"
                    type="submit"
                    disabled={isDisable}
                  >
                    Submit Agreement
                  </Button>
                </ProtectedAction>
              </Flex>
            </Flex>
          </Box>
        </Flex>
      </form>
    </FormProvider>
  );
};

export default SubmittingAuthorOnlyCopyrightForm;
